import { useCallback, useMemo } from "react";
import {
  useDiscountCodeCreateMutation,
  DISCOUNT_CODES_QUERY,
} from "@ticketingplatform/api/dist/discountCode";
import { Form, message } from "antd";
import useApolloErrorFields from "hooks/useApolloErrorFields";
import { useIntl } from "react-intl";
import { useVenueContext } from "contexts/VenueContext";
import { useNavigate } from "react-router-dom";
import { ROUTE_PATHS } from "const/paths";
import usePrompt from "hooks/usePrompt";

import { DiscountCodeFormValue } from "./partials/DiscountCodeForm/types";
import {
  UseDiscountCodeCreate,
  UseDiscountCodeCreateReturnType,
} from "./types";
import { initialValues } from "./consts";

export const useDiscountCodeCreate: UseDiscountCodeCreate = () => {
  const intl = useIntl();
  const [form] = Form.useForm();
  const { venue } = useVenueContext();
  const navigate = useNavigate();

  const [discountCodeCreateMutation, { loading, error }] =
    useDiscountCodeCreateMutation({
      evictQueries: [DISCOUNT_CODES_QUERY],
    });

  const fields = useApolloErrorFields(error);

  usePrompt(loading);

  const onFinish = useCallback(
    async ({ input: { events, ...input } }: DiscountCodeFormValue) => {
      try {
        await discountCodeCreateMutation({
          variables: {
            input: {
              ...input,
              eventIds: events.map(({ id }) => id),
              venueId: venue?.id,
            },
          },
        });

        message.success(
          intl.formatMessage({
            defaultMessage: "Discount Code has been added!",
          })
        );

        navigate(ROUTE_PATHS.DiscountCodeList, {
          state: {
            skipBlock: true,
          },
        });
      } catch {
        message.error(
          intl.formatMessage({
            defaultMessage: "Error. Cannot add Discount Code.",
          })
        );
      }
    },
    [discountCodeCreateMutation, venue?.id, intl, navigate]
  );

  return useMemo<UseDiscountCodeCreateReturnType>(
    () => ({
      form: {
        initialValues,
        fields,
        loading,
        form,
        onFinish,
      },
    }),
    [fields, form, loading, onFinish]
  );
};
